import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { OrganizationRole } from 'src/domain/organization';
import {
    getPrimaryMembership,
    getPrimaryRole,
    UserMembership,
    UserRole,
    isOrganizationAdministrator,
} from 'src/domain/user';

import { useCurrentOrganization } from './useCurrentOrganization';
import { useSignedInUser } from './useSignedInUser';

type CreatorRoleContextValue = {
    isCreator: boolean;
    roles: OrganizationRole[];
    activeRoleId?: string;
    setActiveRoleId: (roleId?: string) => void;
    activeRoleGroupIds: string[];
    isLoading: boolean;
};

const defaultContext: CreatorRoleContextValue = {
    isCreator: false,
    roles: [],
    activeRoleId: undefined,
    setActiveRoleId: () => undefined,
    activeRoleGroupIds: [],
    isLoading: false,
};

const CreatorRoleContext =
    createContext<CreatorRoleContextValue>(defaultContext);

const resolveRoleIds = (membership?: UserMembership): string[] => {
    const fallbackRoleId = (membership as { roleId?: string })?.roleId;
    if (membership?.roleIds?.length) {
        return membership.roleIds.filter(Boolean);
    }
    return fallbackRoleId ? [fallbackRoleId] : [];
};

export const CreatorRoleProvider = ({ children }: { children: ReactNode }) => {
    const user = useSignedInUser();
    const role = getPrimaryRole(user);
    const isCreator =
        role === UserRole.creator && !isOrganizationAdministrator(user);
    const membership = useMemo(() => getPrimaryMembership(user), [user]);
    const { organization, isLoading: isOrganizationLoading } =
        useCurrentOrganization();

    const roles = useMemo(() => {
        const roleIds = resolveRoleIds(membership);
        if (!roleIds.length) {
            return [];
        }
        const roleSource = membership?.roles ?? organization?.roles ?? [];
        return roleSource.filter(roleEntry => roleIds.includes(roleEntry.id));
    }, [membership, organization?.roles]);

    const [activeRoleId, setActiveRoleId] = useState<string | undefined>();

    useEffect(() => {
        if (!isCreator) {
            setActiveRoleId(undefined);
            return;
        }

        if (!roles.length) {
            setActiveRoleId(undefined);
            return;
        }

        if (
            activeRoleId &&
            roles.some(roleEntry => roleEntry.id === activeRoleId)
        ) {
            return;
        }

        setActiveRoleId(roles[0].id);
    }, [activeRoleId, isCreator, roles]);

    const activeRoleGroupIds = useMemo(() => {
        if (!isCreator) {
            return [];
        }

        const fallbackRoleIds = resolveRoleIds(membership);
        const roleIds = activeRoleId ? [activeRoleId] : fallbackRoleIds;

        if (!roleIds.length && !membership?.groupIds?.length) {
            return [];
        }

        const roleSource = membership?.roles ?? organization?.roles ?? [];
        const roleGroupIds = roleIds.flatMap(roleId => {
            const roleDefinition = roleSource.find(
                roleEntry => roleEntry.id === roleId,
            );
            return roleDefinition?.groupIds?.filter(Boolean) ?? [];
        });

        const baseGroupIds =
            (roleGroupIds.length ? roleGroupIds : undefined) ??
            membership?.groupIds ??
            [];

        if (!baseGroupIds.length) {
            return [];
        }

        if (!organization?.groups?.length) {
            return baseGroupIds;
        }

        const childrenByParent = new Map<string, string[]>();

        for (const group of organization.groups) {
            if (!group.parentId) {
                continue;
            }

            const children = childrenByParent.get(group.parentId) ?? [];
            children.push(group.id);
            childrenByParent.set(group.parentId, children);
        }

        const expandedGroupIds = new Set<string>();
        const stack = [...baseGroupIds];

        while (stack.length) {
            const current = stack.pop();
            if (!current || expandedGroupIds.has(current)) {
                continue;
            }

            expandedGroupIds.add(current);

            const children = childrenByParent.get(current);
            if (children?.length) {
                stack.push(...children);
            }
        }

        return Array.from(expandedGroupIds);
    }, [
        activeRoleId,
        isCreator,
        membership,
        organization?.groups,
        organization?.roles,
    ]);

    const value = useMemo(
        () => ({
            isCreator,
            roles,
            activeRoleId,
            setActiveRoleId,
            activeRoleGroupIds,
            isLoading: isCreator && isOrganizationLoading,
        }),
        [
            activeRoleGroupIds,
            activeRoleId,
            isCreator,
            isOrganizationLoading,
            roles,
        ],
    );

    return (
        <CreatorRoleContext.Provider value={value}>
            {children}
        </CreatorRoleContext.Provider>
    );
};

export const useCreatorRoleContext = () => useContext(CreatorRoleContext);
